'use client';

import { useState } from 'react';
import { PasswordVisibilityIcon, PasswordVisibilityOffIcon } from '../../Icons/Icons';
import { Input } from '../../ui/input';

interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
}

/**
 * The email/password ("local") auth forms — login, registration, reset-with-token —
 * each independently reimplemented an eye-icon show/hide toggle over a password input.
 * One shared component instead of four to six copies of the same markup + state.
 */
export function PasswordField({
  id,
  value,
  onChange,
  onFocus,
  onKeyUp,
  placeholder = '',
  hasError = false,
  className = '',
}: PasswordFieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        type={showPassword ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        className={`min-h-[40px] touch-manipulation pr-10 ${hasError ? 'border-destructive cs-input-ring-error' : ''} ${className}`}
      />
      <button
        type="button"
        onClick={() => setShowPassword((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-icon-medium hover:text-icon-strong transition-colors"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <PasswordVisibilityOffIcon /> : <PasswordVisibilityIcon />}
      </button>
    </div>
  );
}
